import React, { useState, useCallback, useEffect, memo } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import Helmet from 'react-helmet';

import * as mainApprovalActions from '../actions/approval/main';
import connectOptions, { mergeProps } from '../utils/connectOptions';
import { PreviewOnlineApprovalLists } from '../components/OnlineApprovalLists';
import { Spinner } from '../components/helpers/Spinner';
import { showError, showInformation } from '../utils/MessagePopup';
import { getApprovalMailToLink } from '../utils/getApprovalMailToLink';
import copyToClipboard from '../utils/copyToClipboard';
import { usePopupContext } from '../contexts/PopupContext';

/**
 * Generate Preview Online Approval Lists component.
 */
const PreviewOnlineApprovalListsContainer = ({
  history,
  match,
  onlineApprovalLoading,
  onlineApproval,
  linksSending,
  loadPreviewOnlineApprovals,
  generateLink,
}) => {
  const { openPopup } = usePopupContext();
  const [link, setLink] = useState('');
  const [expirationDate, setExpirationDate] = useState('');
  const [emailLink, setEmailLink] = useState('');
  const [emails, setEmails] = useState([]);

  useEffect(() => {
    const { params } = match;
    const projectId = parseInt(params.projectId, 10);
    const approvalId = parseInt(params.approvalId, 10) || params.approvalId;

    loadPreviewOnlineApprovals(projectId, approvalId, history.location.search, 'preview');
  }, []);

  const handleGenerateEmailLink = useCallback(
    (newEmails, currentLink = link, currentExpirationDate = expirationDate) => {
      setEmails(newEmails);

      const emailFields = {
        approvalListLabel: onlineApproval.get('approvalListLabel'),
        buyerLegalName: onlineApproval.get('buyerLegalName'),
        link: currentLink,
        expirationDate: currentExpirationDate,
        emails: newEmails,
      };
      const mailToLink = getApprovalMailToLink(emailFields);

      setEmailLink(mailToLink);
    },
    [onlineApproval, link, expirationDate],
  );

  const handleGenerateLink = useCallback(
    linkData => {
      const { params } = match;

      const optionalFields = new URLSearchParams(history.location.search);
      const data = {};

      for (const entry of optionalFields.entries()) {
        data[entry[0]] = Boolean(entry[1]);
      }

      const body = {
        ...data,
        expiresAt: linkData.expiresAt,
      };

      generateLink({
        projectId: params.projectId,
        approvalId: params.approvalId,
        body,
        afterSuccess: responseData => {
          setLink(responseData.link);
          setExpirationDate(responseData.expiresAt);

          handleGenerateEmailLink(emails, responseData.link, responseData.expiresAt);

          copyToClipboard(responseData.link);
          showInformation(openPopup, 'Online Approval List link has been created and copied to clipboard.');
        },
        afterError: () => {
          showError(openPopup, ['The given data failed to pass validation.']);
        },
      });
    },
    [match, history, generateLink, emails, handleGenerateEmailLink, openPopup],
  );

  return (
    <div className="full-height">
      <Helmet title="Generate Online Approval Lists" />

      {onlineApprovalLoading ? (
        <Spinner />
      ) : (
        <PreviewOnlineApprovalLists
          emailLink={emailLink}
          onGenerateEmailLink={handleGenerateEmailLink}
          onGenerateLink={handleGenerateLink}
          onlineApproval={onlineApproval}
          sending={linksSending}
        />
      )}
    </div>
  );
};

const mapStateToProps = state => ({
  ...state.approval,
  linksSending: state.approval.common.get('linksSending'),
  onlineApprovalLoading: state.approval.common.get('onlineApprovalLoading'),
  onlineApproval: state.approval.common.get('onlineApproval'),
  user: state.auth.get('user'),
});

const mapDispatchToProps = dispatch => ({
  ...bindActionCreators(mainApprovalActions, dispatch),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
  mergeProps,
  connectOptions,
)(memo(PreviewOnlineApprovalListsContainer));
